import React, { useState } from 'react';
import { Upload, X, CheckCircle, AlertCircle, FileSpreadsheet, Phone, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LeadImportDialog = ({ isOpen, onClose, onSuccess, campaigns = [] }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [showContactOption, setShowContactOption] = useState(false);
  const [contactingLeads, setContactingLeads] = useState(false);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileName = selectedFile.name.toLowerCase();
      if (fileName.endsWith('.csv') || fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
        setFile(selectedFile);
        setResult(null);
        setShowContactOption(false);
      } else {
        toast.error('Please select a CSV or Excel file (.csv, .xlsx)');
      }
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      const fileName = file.name.toLowerCase();
      const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
      const endpoint = isExcel ? 'import-excel' : 'import-csv';

      const response = await fetch(
        `${BACKEND_URL}/api/leads/${endpoint}`,
        {
          method: 'POST',
          body: formData,
        }
      );

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Import failed');
      }
      
      setResult(data);

      if (data.success && data.imported > 0) {
        toast.success(`Successfully imported ${data.imported} leads!`);
        setShowContactOption(true);
        onSuccess();
      }
    } catch (err) {
      console.error('Upload error:', err);
      setResult({ success: false, error: err.message });
      toast.error(err.message || 'Failed to import leads');
    } finally {
      setUploading(false);
    }
  };

  const handleContactLeads = async () => {
    if (!result?.leads?.length || !selectedCampaign) {
      toast.error('Please select a campaign to contact leads');
      return;
    }

    try {
      setContactingLeads(true);
      
      const leadIds = result.leads.map(lead => lead.id);
      
      const response = await fetch(`${BACKEND_URL}/api/leads/bulk-call`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lead_ids: leadIds,
          campaign_id: selectedCampaign
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to initiate calls');
      }

      toast.success(`Initiated ${data.initiated} calls! ${data.skipped} skipped, ${data.failed} failed.`);
      
      setTimeout(() => {
        onClose();
      }, 2000);
      
    } catch (err) {
      console.error('Contact error:', err);
      toast.error(err.message || 'Failed to contact leads');
    } finally {
      setContactingLeads(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    setShowContactOption(false);
    setSelectedCampaign('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={handleClose}>
      <div
        className="bg-card rounded-lg shadow-xl max-w-2xl w-full mx-4 p-6"
        onClick={(e) => e.stopPropagation()}
        data-testid="lead-import-dialog"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <FileSpreadsheet className="w-6 h-6 text-primary" />
            <h3 className="text-xl font-semibold">Import Leads</h3>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-secondary rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Instructions */}
        <div className="mb-6 p-4 rounded-lg bg-muted">
          <h4 className="font-semibold text-sm mb-2">Supported Formats</h4>
          <div className="flex gap-4 mb-3">
            <span className="px-2 py-1 rounded bg-green-500/10 text-green-600 text-xs font-medium">CSV</span>
            <span className="px-2 py-1 rounded bg-ink-500 text-gold text-xs font-medium">Excel (.xlsx)</span>
          </div>
          <p className="text-sm text-muted-foreground mb-2">
            Your file must include these columns:
          </p>
          <code className="text-xs bg-background p-2 rounded block">
            first_name, last_name, phone, email (optional), vehicle_interest (optional)
          </code>
        </div>

        {/* File Input */}
        <div className="mb-6">
          <label className="block mb-2 text-sm font-medium">Select File</label>
          <div className="relative">
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileChange}
              className="w-full px-4 py-3 rounded-lg border bg-background cursor-pointer
                file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0
                file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground
                hover:file:bg-primary/90"
              disabled={uploading}
              data-testid="file-input"
            />
          </div>
          {file && (
            <p className="text-sm text-muted-foreground mt-2 flex items-center gap-2">
              <FileSpreadsheet className="w-4 h-4" />
              {file.name} ({(file.size / 1024).toFixed(2)} KB)
            </p>
          )}
        </div>

        {/* Result */}
        {result && (
          <div
            className={`mb-6 p-4 rounded-lg ${
              result.success
                ? 'bg-green-500/10 border border-green-500/20'
                : 'bg-red-500/10 border border-red-500/20'
            }`}
          >
            <div className="flex items-start gap-3">
              {result.success ? (
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
              )}
              <div className="flex-1">
                <h4 className="font-semibold text-sm mb-1">
                  {result.success ? 'Import Successful' : 'Import Failed'}
                </h4>
                {result.success && (
                  <p className="text-sm">
                    Imported {result.imported} leads
                    {result.errors && result.errors.length > 0 &&
                      ` (${result.errors.length} errors)`}
                  </p>
                )}
                {result.error && <p className="text-sm text-red-600">{result.error}</p>}
                {result.errors && result.errors.length > 0 && (
                  <div className="mt-2 space-y-1">
                    <p className="text-sm font-medium">Errors:</p>
                    {result.errors.slice(0, 5).map((err, idx) => (
                      <p key={idx} className="text-xs text-muted-foreground">
                        Row {err.row}: {err.error}
                      </p>
                    ))}
                    {result.errors.length > 5 && (
                      <p className="text-xs text-muted-foreground">... and {result.errors.length - 5} more</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Contact Leads Option */}
        {showContactOption && result?.leads?.length > 0 && (
          <div className="mb-6 p-4 rounded-lg bg-primary/5 border border-primary/20">
            <div className="flex items-center gap-3 mb-3">
              <Phone className="w-5 h-5 text-primary" />
              <h4 className="font-semibold">Contact Imported Leads?</h4>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Would you like to start calling these {result.imported} leads now?
            </p>
            
            <div className="mb-4">
              <label className="block mb-2 text-sm font-medium">Select Campaign</label>
              <select
                value={selectedCampaign}
                onChange={(e) => setSelectedCampaign(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border bg-background"
                data-testid="campaign-select"
              >
                <option value="">-- Select a campaign --</option>
                {campaigns.map(campaign => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </option>
                ))}
              </select>
            </div>
            
            <Button
              onClick={handleContactLeads}
              disabled={!selectedCampaign || contactingLeads}
              className="w-full gap-2"
              data-testid="contact-leads-btn"
            >
              {contactingLeads ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Initiating Calls...
                </>
              ) : (
                <>
                  <Phone className="w-4 h-4" />
                  Contact {result.imported} Leads Now
                </>
              )}
            </Button>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={uploading || contactingLeads}
            className="flex-1"
          >
            {showContactOption ? 'Close' : 'Cancel'}
          </Button>
          {!showContactOption && (
            <Button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="flex-1 gap-2"
              data-testid="import-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Import Leads
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadImportDialog;
